import type { CSSProperties } from 'react';

import palette from './grade-palette.json';

import type { GradeCode } from './grade';

/**
 * TRAD-3156 — 등급 색의 **유일한 출처**. `grade-palette.json` 은 docs-ops `design/grade-palette.json`(d5239d67)의
 * **바이트 동일 사본**이다 — 여기 값을 고치지 말고 docs-ops 정본부터 고친 뒤 사본을 다시 복사한다(정본 md 규칙 1·2).
 *
 * ~~grade.ts STYLE_BY_GRADE(Tailwind 팔레트) · GradeGuidePage GRADE_COLORS · GradeSystemTab color 필드~~ — 3벌이 갈려
 * Master·Maestro 배지가 둘 다 빨강, 아바타 흰 글자 대비 1.92~3.96 이었다. 이제 hex 는 json 에만 있고, 화면은
 * 요소에 CSS 변수(--grade-*)를 싣고 고정 클래스가 그 변수를 읽는다(다크는 `dark:` 변형 — tailwind darkMode 'class').
 *
 * 규칙(정본 rules): 배지 라이트 = textLight on tintLight · 배지 다크 = textDark on tintDark + 1px 테두리 solid ·
 * 아바타 = solid 바탕 + 흰 글자(양 테마 같음) · 모르는 등급은 팔레트 밖(각 화면의 중립 폴백).
 */
export interface GradeColors {
  family: string;
  textLight: string;
  tintLight: string;
  textDark: string;
  tintDark: string;
  solid: string;
}

const UPPER_TO_CODE: Record<string, GradeCode> = {
  BEGINNER: 'Beginner',
  NOVICE: 'Novice',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced',
  EXPERT: 'Expert',
  MASTER: 'Master',
  MAESTRO: 'Maestro',
};

/** 서버 표기(PascalCase) 키로 다시 묶은 정본 팔레트. json 의 키는 대문자(정본 그대로). */
export const GRADE_PALETTE: Record<GradeCode, GradeColors> = Object.fromEntries(
  Object.entries(palette.grades).map(([upper, colors]) => [UPPER_TO_CODE[upper], colors as GradeColors]),
) as Record<GradeCode, GradeColors>;

/** 요소에 싣는 CSS 변수(정본 hex 그대로). */
export const gradeColorVars = (grade: GradeCode): CSSProperties => {
  const c = GRADE_PALETTE[grade];
  return {
    '--grade-text': c.textLight,
    '--grade-tint': c.tintLight,
    '--grade-text-dark': c.textDark,
    '--grade-tint-dark': c.tintDark,
    '--grade-solid': c.solid,
  } as CSSProperties;
};

/** 배지 색 클래스(고정) — 값은 위 변수에서. 다크는 1px 테두리 = solid(정본 badgeDark). 라이트는 테두리 없음. */
export const GRADE_BADGE_COLOR_CLASS =
  'bg-[var(--grade-tint)] text-[var(--grade-text)] dark:bg-[var(--grade-tint-dark)] dark:text-[var(--grade-text-dark)] dark:border dark:border-[var(--grade-solid)]';

/** 아바타·진행 막대 색 클래스(고정) — solid 바탕 + 흰 글자, 양 테마 같음(정본 avatar). */
export const GRADE_AVATAR_COLOR_CLASS = 'bg-[var(--grade-solid)] text-white';

/** 모르는 등급의 아바타 — 팔레트 밖이라 배지 미지값과 같은 중립 토큰(정본 unknownGrade 규칙). */
export const UNKNOWN_GRADE_AVATAR_CLASS = 'bg-muted text-muted-foreground';
